import { Stack } from '@mui/material';
import { z } from 'zod';

import { api } from 'common';

import { OrderJobParamsFormFields, OrderJobParamsSchema } from './Common/Orders';
import { PayorParamsFormFields, PayorParamsSchema } from './Common/Payor';
import {
  type AsynchronousJobVariant,
  NullableStringSchema,
  OptionalStringSchema,
  RequiredStringSchema,
} from './common';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';

import JobKind = api.RpaJob.Kind;

const AuthorizationStatusSchema = z.object({
  note: NullableStringSchema,
  authorizationRequired: NullableStringSchema,
  status: RequiredStringSchema,
  authorizationNumber: NullableStringSchema,
  patientType: RequiredStringSchema,
  requestedDate: NullableStringSchema,
  obtainedDate: NullableStringSchema,
  effectiveDate: NullableStringSchema,
  expirationDate: NullableStringSchema,
  trackingNumber: NullableStringSchema,
  rejectedComment: NullableStringSchema,
  reason: RequiredStringSchema,
  verificationGsPath: NullableStringSchema,
});

function AuthorizationStatusFormFields() {
  return (
    <Stack spacing={2}>
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.note"
        label="Note"
        helperText="Enter the reference to the Note. e.g. `{{authorization.note}}`"
        placeholder="e.g. {{authorization.note}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.authorizationRequired"
        label="Authorization Required"
        helperText="Enter the reference to the Authorization Required. e.g. `{{authorization.authorizationRequired}}`"
        placeholder="e.g. {{authorization.authorizationRequired}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.status"
        label="Status"
        helperText="Enter the reference to the Status. e.g. `{{authorization.status}}`"
        placeholder="e.g. {{authorization.status}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.authorizationNumber"
        label="Authorization Number"
        helperText="Enter the reference to the Authorization Number. e.g. `{{authorization.authorizationNumber}}`"
        placeholder="e.g. {{authorization.authorizationNumber}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.patientType"
        label="Patient Type"
        helperText="Enter the reference to the Patient Type. e.g. `{{authorization.patientType}}`"
        placeholder="e.g. {{authorization.patientType}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.requestedDate"
        label="Requested Date"
        helperText="Enter the reference to the Requested Date. e.g. `{{authorization.requestedDate}}`"
        placeholder="e.g. {{authorization.requestedDate}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.obtainedDate"
        label="Obtained Date"
        helperText="Enter the reference to the Obtained Date. e.g. `{{authorization.obtainedDate}}`"
        placeholder="e.g. {{authorization.obtainedDate}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.effectiveDate"
        label="Effective Date"
        helperText="Enter the reference to the Effective Date. e.g. `{{authorization.effectiveDate}}`"
        placeholder="e.g. {{authorization.effectiveDate}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.expirationDate"
        label="Expiration Date"
        helperText="Enter the reference to the Expiration Date. e.g. `{{authorization.expirationDate}}`"
        placeholder="e.g. {{authorization.expirationDate}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.trackingNumber"
        label="Tracking Number"
        helperText="Enter the reference to the Tracking Number. e.g. `{{authorization.trackingNumber}}`"
        placeholder="e.g. {{authorization.trackingNumber}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.rejectedComment"
        label="Rejected Comment"
        helperText="Enter the reference to the Rejected Comment. e.g. `{{authorization.rejectedComment}}`"
        placeholder="e.g. {{authorization.rejectedComment}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.reason"
        label="Reason"
        helperText="Enter the reference to the Reason. e.g. `{{authorization.reason}}`"
        placeholder="e.g. {{authorization.reason}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.verificationGsPath"
        label="Verification GS Path"
        helperText="Enter the reference to the Verification GS Path. e.g. `{{authorization.verificationGsPath}}`"
        placeholder="e.g. {{authorization.verificationGsPath}}"
      />
    </Stack>
  );
}

export const ExportAuthorizationStatusVariant: AsynchronousJobVariant = {
  schema: z.object({
    jobKind: z.literal(JobKind.ExportAuthorizationStatus),
    payload: OrderJobParamsSchema.merge(PayorParamsSchema).merge(
      z.object({
        authorizationStatus: AuthorizationStatusSchema,
        authorizationSubmissions: OptionalStringSchema.default('[]'),
        cptCode: NullableStringSchema,
        rawProcedures: NullableStringSchema,
        referredToDepartment: NullableStringSchema,
        referredByDepartment: NullableStringSchema,
      })
    ),
  }),
  Config: () => {
    return (
      <Stack spacing={2}>
        <OrderJobParamsFormFields />
        <PayorParamsFormFields />
        <AuthorizationStatusFormFields />
        <BlockValueTemplateField
          fieldName="config.payload.authorizationSubmissions"
          label="Authorization Submissions"
          helperText="Enter the reference to the Authorization Submissions. e.g. `{{authorization.authorizationSubmissions}}`"
          placeholder="e.g. {{authorization.authorizationSubmissions}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.cptCode"
          label="CPT Code"
          helperText="Enter the reference to the CPT Code. e.g. `{{authorization.cptCode}}`"
          placeholder="e.g. {{authorization.cptCode}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.rawProcedures"
          label="Raw Procedures"
          helperText="Enter the reference to the Raw Procedures. e.g. `{{authorization.rawProcedures}}`"
          placeholder="e.g. {{authorization.rawProcedures}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredToDepartment"
          label="Referred To Department"
          helperText="Enter the reference to the Referred To Department. e.g. `{{authorization.referredToDepartment}}`"
          placeholder="e.g. {{authorization.referredToDepartment}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByDepartment"
          label="Referred By Department"
          helperText="Enter the reference to the Referred By Department. e.g. `{{authorization.referredByDepartment}}`"
          placeholder="e.g. {{authorization.referredByDepartment}}"
        />
      </Stack>
    );
  },
};

const AuthorizationStatusSchemaV2 = z.object({
  note: NullableStringSchema,
  authorizationRequired: RequiredStringSchema,
  status: RequiredStringSchema,
  authorizationNumber: NullableStringSchema,
  patientType: RequiredStringSchema,
  requestedDate: NullableStringSchema,
  obtainedDate: NullableStringSchema,
  effectiveDate: NullableStringSchema,
  expirationDate: NullableStringSchema,
  trackingNumber: NullableStringSchema,
  rejectedComment: NullableStringSchema,
  reason: RequiredStringSchema,
  verificationGsPath: NullableStringSchema,
});

function AuthorizationStatusFormFieldsV2() {
  return (
    <Stack spacing={2}>
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.note"
        label="Note"
        helperText="Enter the reference to the Note. e.g. `{{authorization.note}}`"
        placeholder="e.g. {{authorization.note}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.authorizationRequired"
        label="Authorization Required"
        helperText="Enter the reference to the Authorization Required. e.g. `{{authorization.authorizationRequired}}`"
        placeholder="e.g. {{authorization.authorizationRequired}}"
        required
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.status"
        label="Status"
        helperText="Enter the reference to the Status. e.g. `{{authorization.status}}`"
        placeholder="e.g. {{authorization.status}}"
        required
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.authorizationNumber"
        label="Authorization Number"
        helperText="Enter the reference to the Authorization Number. e.g. `{{authorization.authorizationNumber}}`"
        placeholder="e.g. {{authorization.authorizationNumber}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.patientType"
        label="Patient Type"
        helperText="Enter the reference to the Patient Type. e.g. `{{authorization.patientType}}`"
        placeholder="e.g. {{authorization.patientType}}"
        required
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.requestedDate"
        label="Requested Date"
        helperText="Enter the reference to the Requested Date. e.g. `{{authorization.requestedDate}}`"
        placeholder="e.g. {{authorization.requestedDate}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.obtainedDate"
        label="Obtained Date"
        helperText="Enter the reference to the Obtained Date. e.g. `{{authorization.obtainedDate}}`"
        placeholder="e.g. {{authorization.obtainedDate}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.effectiveDate"
        label="Effective Date"
        helperText="Enter the reference to the Effective Date. e.g. `{{authorization.effectiveDate}}`"
        placeholder="e.g. {{authorization.effectiveDate}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.expirationDate"
        label="Expiration Date"
        helperText="Enter the reference to the Expiration Date. e.g. `{{authorization.expirationDate}}`"
        placeholder="e.g. {{authorization.expirationDate}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.trackingNumber"
        label="Tracking Number"
        helperText="Enter the reference to the Tracking Number. e.g. `{{authorization.trackingNumber}}`"
        placeholder="e.g. {{authorization.trackingNumber}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.rejectedComment"
        label="Rejected Comment"
        helperText="Enter the reference to the Rejected Comment. e.g. `{{authorization.rejectedComment}}`"
        placeholder="e.g. {{authorization.rejectedComment}}"
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.reason"
        label="Reason"
        helperText="Enter the reference to the Reason. e.g. `{{authorization.reason}}`"
        placeholder="e.g. {{authorization.reason}}"
        required
      />
      <BlockValueTemplateField
        fieldName="config.payload.authorizationStatus.verificationGsPath"
        label="Verification GS Path"
        helperText="Enter the reference to the Verification GS Path. e.g. `{{authorization.verificationGsPath}}`"
        placeholder="e.g. {{authorization.verificationGsPath}}"
      />
    </Stack>
  );
}

export const ExportAuthorizationStatusVariantV2: AsynchronousJobVariant = {
  schema: z.object({
    jobKind: z.literal(JobKind.ExportAuthorizationStatusV2),
    payload: OrderJobParamsSchema.merge(PayorParamsSchema).merge(
      z.object({
        authorizationStatus: AuthorizationStatusSchemaV2,
        authorizationSubmissions: OptionalStringSchema,
        cptCode: NullableStringSchema,
        rawProcedures: NullableStringSchema,
        referredToDepartment: NullableStringSchema,
        referredByDepartment: NullableStringSchema,
      })
    ),
  }),
  Config: () => {
    return (
      <Stack spacing={2}>
        <OrderJobParamsFormFields />
        <PayorParamsFormFields />
        <AuthorizationStatusFormFieldsV2 />
        <BlockValueTemplateField
          fieldName="config.payload.authorizationSubmissions"
          label="Authorization Submissions"
          helperText="Enter the reference to the Authorization Submissions. e.g. `{{authorization.authorizationSubmissions}}`"
          placeholder="e.g. {{authorization.authorizationSubmissions}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.cptCode"
          label="CPT Code"
          helperText="Enter the reference to the CPT Code. e.g. `{{authorization.cptCode}}`"
          placeholder="e.g. {{authorization.cptCode}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.rawProcedures"
          label="Raw Procedures"
          helperText="Enter the reference to the Raw Procedures. e.g. `{{authorization.rawProcedures}}`"
          placeholder="e.g. {{authorization.rawProcedures}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredToDepartment"
          label="Referred To Department"
          helperText="Enter the reference to the Referred To Department. e.g. `{{authorization.referredToDepartment}}`"
          placeholder="e.g. {{authorization.referredToDepartment}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByDepartment"
          label="Referred By Department"
          helperText="Enter the reference to the Referred By Department. e.g. `{{authorization.referredByDepartment}}`"
          placeholder="e.g. {{authorization.referredByDepartment}}"
        />
      </Stack>
    );
  },
};
